/*
  Kiwi Ears · spotlight rail engine (Rene 2026-08-28; generalised same day —
  "save this block for other uses").

  One engine, any number of horizontal one-thing-per-screen bands. Current
  consumers: sections/kh-spotlight.liquid (the person + house-card rail built
  for the creator hub first) and sections/cr-work.liquid (the full-bleed photo
  columns from Figma 536:108). A band opts in with data-spot on its root,
  data-spot-view / data-spot-track for the window/train pair, data-spot-panel
  per screen, and optionally data-spot-tab / data-spot-progress.

  A sibling of assets/kiwi-timeline.js, deliberately. That file already solved
  horizontal-scroll-on-pin for this site and its decisions were argued out
  there; re-deriving them here would give the site two rails that behave
  differently on the same wheel. What is shared is copied on purpose:

    · TWO EXCLUSIVE MODES. Native overflow-x is the floor — it works with no JS
      at all, it is the phone behaviour, and it is what reduced motion gets. On
      desktop the band flips to data-spot-mode="pinned", ScrollTrigger pins it
      and the TRACK is translated instead. Never both at once: two origins for
      one position make the progress readout lie.
    · ease:'none'. Not taste — with any other ease the scroll position and the
      horizontal position drift apart and the rail feels like it is fighting
      the wheel.
    · scrub:true, not a number. Lenis already smooths the scroll (assets/app.js
      binds lenis.on('scroll', ScrollTrigger.update) and drives lenis.raf from
      the GSAP ticker); a numeric scrub on top double-smooths the travel into
      mush. There is no scrollerProxy here for the same reason — adding one
      would double-drive the scroller.
    · refreshPriority, because these bands sit below other ScrollTriggers on
      their pages and refresh otherwise runs in creation order, which gets a
      late pin's spacing wrong.

  What differs from the timeline:
    · the active panel is whichever one covers the middle of the view, not a
      marker crossing a third-line — panels here are near-screen-width, so the
      centre is the honest test.
    · a debounced rebuild on resize AND orientationchange. The pin's travel is
      derived from viewport width and the mode itself flips at 1024px; the
      usage panel shipped without this and had to be fixed after the fact
      (Rene 2026-08-27), so it is here from the start.
    · no URL state. These are bands you pass through, not destinations.

  Degrades in one direction only: no GSAP, no ScrollTrigger, reduced motion, a
  narrow viewport, or a rail short enough to fit — any of those and the strip is
  simply swipeable, which is the whole content with none of the machinery.
*/
(function () {
  if (window.__kiwiSpotRailReady) return;
  window.__kiwiSpotRailReady = true;

  var still = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* Everything below is PER BAND — two rails on one page must not share a
     ScrollTrigger, an active index, or a resize rebuild. */
  function setup(root) {
  var view = root.querySelector('[data-spot-view]');
  var track = root.querySelector('[data-spot-track]');
  var panels = [].slice.call(root.querySelectorAll('[data-spot-panel]'));
  var tabs = [].slice.call(root.querySelectorAll('[data-spot-tab]'));
  var pctEl = root.querySelector('[data-spot-progress]');
  if (!view || !track || !panels.length) return;

  var active = -1;

  /* ---------------------------------------------------------- position */

  /* MEASURE ONCE, READ FROM CACHE. The first version called
     track.scrollWidth / view.clientWidth / panel.offsetLeft inside the pin's
     onUpdate — a forced reflow on every scroll tick, interleaved with the
     transform GSAP was writing to the same track. That read/write thrash was
     visible as lag the moment the pin engaged (Rene 2026-08-28). Layout only
     changes on resize/refresh, so everything the hot path needs is cached
     here and recomputed in measure(), which runs at build time and from
     ScrollTrigger's onRefresh. */
  var extent = 0;      /* how far the track can travel */
  var viewMid = 0;     /* half the view width */
  var offsets = [];    /* each panel's offsetLeft */

  function measure() {
    extent = Math.max(0, track.scrollWidth - view.clientWidth);
    viewMid = view.clientWidth / 2;
    offsets = panels.map(function (p) { return p.offsetLeft; });
    return extent;
  }
  function maxPan() { return extent; }

  /* Which panel the reader is ON at pan offset x: the one covering the middle
     of the view. One panel fills the screen, so the centre is the honest test —
     a leading-edge test flips to the next panel while most of the current one
     is still on screen. Cache-only: no DOM reads in here. */
  function panelAt(x) {
    var line = x + viewMid;
    var i = 0;
    for (var n = 0; n < offsets.length; n++) {
      if (offsets[n] <= line) i = n;
    }
    return i;
  }

  var lastPct = -1;
  function setPct(p) {
    if (!pctEl) return;
    var v = Math.round(Math.max(0, Math.min(1, p)) * 100);
    if (v === lastPct) return;   /* skip the DOM write when nothing changed */
    lastPct = v;
    pctEl.textContent = '(' + v + '%)';
  }

  function setActive(i) {
    i = Math.max(0, Math.min(panels.length - 1, i));
    if (i === active) return;
    active = i;
    tabs.forEach(function (t, n) {
      if (n === i) t.setAttribute('aria-current', 'true');
      else t.removeAttribute('aria-current');
    });
  }

  /* Both modes funnel through here: x is the current pan in px, however it
     was produced. */
  function onPan(x) {
    var m = maxPan();
    setPct(m ? x / m : 0);
    setActive(panelAt(x));
  }

  /* ------------------------------------------------------------- modes */

  var jumpTo = null;
  var pinnedMode = false;

  /* Wired unconditionally but inert while pinned: the pin sets overflow hidden
     and scrollLeft stays 0, and the flag keeps a stray scroll event (an anchor
     jump, a find-in-page) from fighting the pin's readout. */
  view.addEventListener('scroll', function () {
    if (pinnedMode) return;
    onPan(view.scrollLeft);
  }, { passive: true });

  function armNative() {
    pinnedMode = false;
    measure();
    root.setAttribute('data-spot-mode', 'native');
    /* tabindex only in this mode — a focusable div that cannot scroll is a
       dead tab stop, and in pinned mode the view cannot scroll. */
    view.setAttribute('tabindex', '0');
    jumpTo = function (i, instant) {
      var x = Math.min(offsets[i], maxPan());
      if (view.scrollTo) view.scrollTo({ left: x, behavior: instant || still ? 'auto' : 'smooth' });
      else view.scrollLeft = x;
    };
    onPan(view.scrollLeft);
  }

  var st = null;

  function armPin() {
    var g = window.gsap, ST = window.ScrollTrigger;
    if (!g || !ST || still) return false;
    /* Pins are desktop-only by house rule — except where Rene has explicitly
       called a mobile pin for a specific band (CLAUDE.md lists pk-usage; the
       creator collaborations joined 2026-08-28: "it should scroll as the
       person moves down" on phones too). Such a band carries
       data-spot-pin="all"; everything else keeps the native swipe strip
       below 1024px. Reduced motion still wins over the attribute. */
    if (root.getAttribute('data-spot-pin') !== 'all' &&
        window.matchMedia && window.matchMedia('(max-width: 1023px)').matches) return false;
    if (measure() <= 0) return false;

    pinnedMode = true;
    root.setAttribute('data-spot-mode', 'pinned');
    root.removeAttribute('tabindex');
    view.removeAttribute('tabindex');
    view.scrollLeft = 0;

    /* THE DWELL. The travel used to begin on the very first pixel of the pin,
       so arriving at the band immediately slid panel one away — you could
       never actually read it (Rene 2026-08-28). The first half-screen of
       pinned scroll now holds the track still; only past that does vertical
       scroll become horizontal travel. Half a screen is enough to settle and
       read without the band feeling stuck to someone scrolling straight
       through.

       Standalone ScrollTrigger.create + a quickSetter instead of a scrubbed
       tween: the dwell needs a piecewise mapping from progress to pan, and
       computing it in onUpdate then writing through one retargetable setter
       is the skill's fast path for a value that changes every tick. The
       write is transform-only; all values it reads are cached by
       distances(), which the end() function re-runs on every refresh. */
    var setX = g.quickSetter(track, 'x', 'px');
    var holdPx = 0, panDist = 0, totalDist = 0;
    function distances() {
      measure();
      holdPx = Math.round(window.innerHeight * 0.5);
      /* travel distance floored at a screen so a two-panel rail still pins
         long enough to read */
      panDist = Math.max(extent, window.innerHeight);
      totalDist = holdPx + panDist;
      return totalDist;
    }

    st = ST.create({
      trigger: root,
      start: 'top top',
      end: function () { return '+=' + distances(); },
      pin: true,
      refreshPriority: 1,
      /* cache-only from here down — the scroll hot path does no layout reads */
      onUpdate: function (self) {
        var scrolled = self.progress * totalDist;
        var frac = Math.max(0, Math.min(1, (scrolled - holdPx) / panDist));
        var pan = frac * extent;
        setX(-pan);
        onPan(pan);
      }
    });

    jumpTo = function (i, instant) {
      var frac = extent ? Math.min(offsets[i], extent) / extent : 0;
      /* + holdPx: land where the travel begins, past the dwell — tab one
         should show panel one at rest, not the top of the dead zone */
      var y = st.start + holdPx + frac * panDist;
      if (instant) { st.scroll(y); ST.update(); return; }
      if (window.lenis) window.lenis.scrollTo(y, { duration: 0.9 });
      else window.scrollTo({ top: y, behavior: 'smooth' });
    };

    /* Photographs and fonts change the track's width expectations; refresh
       once they are in so the travel is measured against the real layout. */
    ST.refresh();
    return true;
  }

  tabs.forEach(function (t, n) {
    t.addEventListener('click', function () {
      /* setActive FIRST, then travel. The scroll takes ~0.9s and the pan's
         onUpdate is what would otherwise move the state — a tab clicked while
         pinned sat dead for the whole ride. setActive is idempotent, so the
         onUpdate that arrives as the scroll lands is a no-op and the tab
         lights up on the press instead. */
      setActive(n);
      if (jumpTo) jumpTo(n);
    });
  });

  /* --------------------------------------------------------------- arm */

  function build() {
    if (st) { st.kill(true); st = null; }
    if (window.gsap) window.gsap.set(track, { x: 0 });
    if (!armPin()) armNative();
    setActive(0);
  }

    build();

    /* The pin's travel is a function of viewport width and the mode itself
       flips at 1024px, so a resize has to rebuild rather than just refresh.
       Debounced because a dragged window corner fires this continuously, and
       tearing down a pin mid-drag is both expensive and visibly jumpy. */
    var t;
    function later() {
      clearTimeout(t);
      t = setTimeout(function () {
        var wantsPin = !still && !!window.gsap && !!window.ScrollTrigger &&
          (root.getAttribute('data-spot-pin') === 'all' ||
            !(window.matchMedia && window.matchMedia('(max-width: 1023px)').matches)) &&
          measure() > 0;
        if (wantsPin !== pinnedMode) build();
        else if (window.ScrollTrigger) window.ScrollTrigger.refresh();
      }, 220);
    }
    window.addEventListener('resize', later, { passive: true });
    window.addEventListener('orientationchange', later, { passive: true });
  }

  function arm() {
    [].slice.call(document.querySelectorAll('[data-spot]')).forEach(setup);
  }

  if (document.readyState === 'complete') arm();
  else window.addEventListener('load', arm);
})();
